if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.prevButton = this.querySelector('[name="previous"]');
        this.nextButton = this.querySelector('[name="next"]');
        this.loader = this.querySelector('.product-media-modal__loader');
        this.loaderToken = 0;
        this.zoom = { scale: 1, x: 0, y: 0 };
        this.pinch = null;
        this.pan = null;
        this.swipe = null;
        this.zoomDismissed = false;
        this.bindNav(this.prevButton, -1);
        this.bindNav(this.nextButton, 1);
        this.bindPinchZoom();
      }

      hide() {
        super.hide();
        this.resetZoom();
        this.hideLoader();
      }

      show(opener) {
        this.zoomDismissed = false;
        this.resetZoom();
        this.syncToGallery();
        super.show(opener);
        this.showActiveMedia();
      }

      mobileZoomQuery() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      activeZoomImage() {
        return this.querySelector('.product-media-modal__content > img.active');
      }

      imageFrame() {
        const img = this.activeZoomImage();
        const content = this.querySelector('.product-media-modal__content');
        if (!img || !content) return null;
        const parent = content.getBoundingClientRect();
        return {
          cx: parent.left + parent.width / 2,
          cy: parent.top + parent.height / 2,
          w: img.offsetWidth,
          h: img.offsetHeight,
          parentW: parent.width,
          parentH: parent.height,
        };
      }

      touchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.hypot(dx, dy);
      }

      touchMid(touches) {
        return {
          x: (touches[0].clientX + touches[1].clientX) / 2,
          y: (touches[0].clientY + touches[1].clientY) / 2,
        };
      }

      bindPinchZoom() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        content.addEventListener('touchstart', (event) => this.onZoomStart(event), { passive: true });
        content.addEventListener('touchmove', (event) => this.onZoomMove(event), { passive: false });
        content.addEventListener('touchend', (event) => this.onZoomEnd(event));
        content.addEventListener('touchcancel', (event) => this.onZoomEnd(event));
      }

      onZoomStart(event) {
        if (!this.mobileZoomQuery() || this.zoomDismissed || this.swipeSettling || !this.activeZoomImage()) return;

        if (event.touches.length === 2) {
          const frame = this.imageFrame();
          if (!frame) return;
          const mid = this.touchMid(event.touches);
          this.pan = null;
          if (this.swipe) this.clearSwipePlacement();
          this.swipe = null;
          this.pinch = {
            startDist: this.touchDistance(event.touches),
            startScale: this.zoom.scale,
            wasOverFit: false,
            localX: (mid.x - frame.cx - this.zoom.x) / this.zoom.scale,
            localY: (mid.y - frame.cy - this.zoom.y) / this.zoom.scale,
          };
          return;
        }

        if (event.touches.length === 1 && this.zoom.scale > 1) {
          this.swipe = null;
          this.pan = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
            originX: this.zoom.x,
            originY: this.zoom.y,
          };
          return;
        }

        if (event.touches.length === 1 && this.zoom.scale <= 1) {
          const touch = event.touches[0];
          this.swipe = { x: touch.clientX, y: touch.clientY, lastX: touch.clientX, lastY: touch.clientY };
        }
      }

      onZoomMove(event) {
        if (!this.hasAttribute('open')) return;
        if (this.mobileZoomQuery() && event.touches.length > 1 && event.cancelable) {
          event.preventDefault();
        }
        if (this.zoomDismissed || !this.activeZoomImage()) return;

        if (event.touches.length === 2 && this.pinch && this.pinch.startDist > 0) {
          if (event.cancelable) event.preventDefault();
          const frame = this.imageFrame();
          if (!frame) return;

          let next = this.pinch.startScale * (this.touchDistance(event.touches) / this.pinch.startDist);
          if (next > 1 + 1 / 15) this.pinch.wasOverFit = true;

          const closing = next < 1 && !this.pinch.wasOverFit && this.pinch.startScale <= 1;
          if (closing) {
            this.zoom.scale = Math.max(0.4, next);
            const opacity = Math.max(0, 1 - (1 - this.zoom.scale) * 1.2);
            this.setChromeOpacity(opacity, false);
          } else if (next < 1) {
            this.zoom.scale = 1 - 0.15 * (1 - next);
            this.setChromeOpacity(1, false);
          } else if (next > 4) {
            this.zoom.scale = 4 + 0.05 * (next - 4);
            this.setChromeOpacity(1, false);
          } else {
            this.zoom.scale = next;
            this.setChromeOpacity(1, false);
          }

          const mid = this.touchMid(event.touches);
          this.zoom.x = mid.x - frame.cx - this.pinch.localX * this.zoom.scale;
          this.zoom.y = mid.y - frame.cy - this.pinch.localY * this.zoom.scale;
          this.applyZoom(false);
          return;
        }

        if (event.touches.length === 1 && this.swipe && this.zoom.scale <= 1) {
          const touch = event.touches[0];
          const dx = touch.clientX - this.swipe.x;
          const dy = touch.clientY - this.swipe.y;
          this.swipe.lastX = touch.clientX;
          this.swipe.lastY = touch.clientY;
          if (!this.swipe.locked) {
            if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
            this.swipe.locked = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
            if (this.swipe.locked === 'horizontal') this.prepareSwipeNeighbors();
          }
          if (this.swipe.locked !== 'horizontal') return;
          if (event.cancelable) event.preventDefault();
          this.swipe.shift = this.resistedSwipe(dx);
          this.applySwipeShift(this.swipe.shift, false);
          return;
        }

        if (event.touches.length === 1 && this.pan && this.zoom.scale > 1) {
          if (event.cancelable) event.preventDefault();
          const touch = event.touches[0];
          this.zoom.x = this.pan.originX + (touch.clientX - this.pan.x);
          this.zoom.y = this.pan.originY + (touch.clientY - this.pan.y);
          this.clampPan();
          this.applyZoom(false);
        }
      }

      onZoomEnd(event) {
        if (this.zoomDismissed) return;

        if (event.touches.length === 1 && this.zoom.scale > 1) {
          this.pinch = null;
          this.pan = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
            originX: this.zoom.x,
            originY: this.zoom.y,
          };
          return;
        }

        if (event.touches.length > 0) return;

        const pinch = this.pinch;
        const swipe = this.swipe;
        this.pinch = null;
        this.pan = null;
        this.swipe = null;
        if (pinch && this.zoom.scale < 1 && !pinch.wasOverFit && pinch.startScale <= 1) {
          this.animateClose();
          return;
        }
        if (!pinch && swipe && this.zoom.scale <= 1 && swipe.locked === 'horizontal') {
          this.settleSwipe(swipe);
          return;
        }
        if (swipe) this.clearSwipePlacement();
        this.correctZoomPan();
      }

      swipeWidth() {
        const content = this.querySelector('.product-media-modal__content');
        return content ? content.clientWidth : window.innerWidth;
      }

      neighborMedia(direction) {
        const items = this.mediaItems();
        const current = this.querySelector('.product-media-modal__content > .active');
        const index = items.indexOf(current);
        if (index < 0) return null;
        return items[index + direction] || null;
      }

      resistedSwipe(dx) {
        if (dx < 0 && !this.neighborMedia(1)) return dx * 0.35;
        if (dx > 0 && !this.neighborMedia(-1)) return dx * 0.35;
        return dx;
      }

      prepareSwipeNeighbors() {
        [-1, 1].forEach((direction) => {
          const el = this.neighborMedia(direction);
          if (el) this.waitForMedia(el);
        });
      }

      placeSwipeImage(el, offset, animate) {
        if (!el) return;
        el.style.display = 'block';
        el.style.position = 'absolute';
        el.style.left = '50%';
        el.style.top = '50%';
        el.style.margin = '0';
        el.style.transition = animate ? 'transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none';
        el.style.transform = `translate(calc(-50% + ${offset}px), -50%)`;
      }

      applySwipeShift(dx, animate) {
        const width = this.swipeWidth();
        const current = this.querySelector('.product-media-modal__content > .active');
        this.placeSwipeImage(current, dx, animate);
        this.placeSwipeImage(this.neighborMedia(-1), dx - width, animate);
        this.placeSwipeImage(this.neighborMedia(1), dx + width, animate);
      }

      clearSwipePlacement() {
        this.querySelectorAll('.product-media-modal__content > *').forEach((el) => {
          el.style.display = '';
          el.style.position = '';
          el.style.left = '';
          el.style.top = '';
          el.style.margin = '';
          el.style.transition = '';
          el.style.transform = '';
        });
      }

      settleSwipe(swipe) {
        const width = this.swipeWidth();
        const shift = swipe.shift || 0;
        const direction = shift < 0 ? 1 : -1;
        const commit = Math.abs(shift) > Math.min(72, width * 0.18) && this.neighborMedia(direction);
        const target = commit ? (direction === 1 ? -width : width) : 0;
        this.swipeSettling = true;
        this.applySwipeShift(target, true);

        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          this.swipeSettling = false;
          if (commit) this.stepMedia(direction);
          else this.clearSwipePlacement();
        };
        const current = this.querySelector('.product-media-modal__content > .active');
        if (current) current.addEventListener('transitionend', finish, { once: true });
        window.setTimeout(finish, 320);
      }

      clampPan() {
        const frame = this.imageFrame();
        if (!frame || this.zoom.scale <= 1) {
          this.zoom.x = 0;
          this.zoom.y = 0;
          return;
        }

        const maxX = Math.max(0, (frame.w * this.zoom.scale - frame.parentW) / 2);
        const maxY = Math.max(0, (frame.h * this.zoom.scale - frame.parentH) / 2);
        this.zoom.x = Math.min(maxX, Math.max(-maxX, this.zoom.x));
        this.zoom.y = Math.min(maxY, Math.max(-maxY, this.zoom.y));
      }

      applyZoom(animate) {
        const img = this.activeZoomImage();
        if (!img) return;
        img.style.transition = animate ? 'transform 320ms cubic-bezier(0.2, 0.9, 0.3, 1)' : 'none';
        const { scale, x, y } = this.zoom;
        img.style.transform = scale === 1 && x === 0 && y === 0 ? '' : `translate(${x}px, ${y}px) scale(${scale})`;
      }

      setChromeOpacity(opacity, animate) {
        this.style.transition = animate ? 'background-color 280ms ease' : 'none';
        this.style.backgroundColor = `rgba(0, 0, 0, ${opacity})`;
        [this.querySelector('.product-media-modal__toggle'), this.prevButton, this.nextButton].forEach((el) => {
          if (!el) return;
          el.style.transition = animate ? 'opacity 280ms ease' : 'none';
          el.style.opacity = String(opacity);
        });
      }

      galleryTargetRect() {
        const img = this.activeZoomImage();
        if (!img) return null;
        const id = img.getAttribute('data-media-id');
        if (!id) return null;
        const galleryImg = document.querySelector(
          `.product__media-list [data-media-id$="-${CSS.escape(id)}"] img, .product__media-list [data-media-id="${CSS.escape(id)}"] img`
        );
        if (!galleryImg) return null;
        const rect = galleryImg.getBoundingClientRect();
        if (rect.width < 2 || rect.height < 2) return null;
        return {
          cx: rect.left + rect.width / 2,
          cy: rect.top + rect.height / 2,
          width: rect.width,
        };
      }

      animateClose() {
        if (this.zoomDismissed) return;
        this.zoomDismissed = true;
        this.pinch = null;
        this.pan = null;

        const img = this.activeZoomImage();
        const frame = this.imageFrame();
        const target = this.galleryTargetRect();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!img || !frame || !target || reduce || frame.w < 2) {
          this.hide();
          return;
        }

        const content = this.querySelector('.product-media-modal__content');
        if (content) content.style.overflow = 'visible';

        const scale = target.width / frame.w;
        const x = target.cx - frame.cx;
        const y = target.cy - frame.cy;
        img.style.transition = 'transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1)';
        img.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
        this.setChromeOpacity(0, true);

        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          this.hide();
        };
        img.addEventListener('transitionend', finish, { once: true });
        window.setTimeout(finish, 340);
      }

      correctZoomPan() {
        if (this.zoom.scale < 1 || this.zoom.scale > 4) {
          this.zoom.scale = Math.min(4, Math.max(1, this.zoom.scale));
        }
        if (this.zoom.scale <= 1) {
          this.zoom.x = 0;
          this.zoom.y = 0;
        } else {
          this.clampPan();
        }
        this.setChromeOpacity(1, true);
        this.applyZoom(true);
      }

      resetZoom() {
        this.zoom = { scale: 1, x: 0, y: 0 };
        this.pinch = null;
        this.pan = null;
        this.swipe = null;
        this.style.backgroundColor = '';
        this.style.transition = '';
        const content = this.querySelector('.product-media-modal__content');
        if (content) content.style.overflow = '';
        [this.querySelector('.product-media-modal__toggle'), this.prevButton, this.nextButton].forEach((el) => {
          if (!el) return;
          el.style.opacity = '';
          el.style.transition = '';
        });
        this.clearSwipePlacement();
      }

      bindNav(button, direction) {
        if (!button) return;
        const go = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.stepMedia(direction);
        };
        button.addEventListener('click', go);
        button.addEventListener('pointerup', (event) => event.stopPropagation());
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        this.updateNav();
        this.waitForMedia(activeMedia);

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      galleryMediaIds() {
        const ids = [];
        document.querySelectorAll('.product__media-list .product__media-item:not(.not-current-variant)').forEach((item) => {
          if (item.getAttribute('data-main-media-type') === 'video') return;
          const id = (item.getAttribute('data-media-id') || '').split('-').pop();
          if (id) ids.push(String(id));
        });
        return ids;
      }

      mediaItems() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];

        const allowed = new Set(this.galleryMediaIds());
        return Array.from(content.children).filter((el) => {
          const id = el.getAttribute('data-media-id');
          if (!id) return false;
          if (!allowed.size) return true;
          return allowed.has(String(id));
        });
      }

      syncToGallery() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        const allowed = this.galleryMediaIds();
        const allowedSet = new Set(allowed);
        const nodes = Array.from(content.querySelectorAll('[data-media-id]'));

        nodes.forEach((el) => {
          const id = String(el.getAttribute('data-media-id') || '');
          el.classList.toggle('not-current-variant', allowedSet.size > 0 && !allowedSet.has(id));
        });

        allowed.forEach((id) => {
          const el = content.querySelector(`[data-media-id="${id}"]`);
          if (el) content.appendChild(el);
        });
        nodes.forEach((el) => {
          const id = String(el.getAttribute('data-media-id') || '');
          if (!allowedSet.has(id)) content.appendChild(el);
        });
      }

      stepMedia(direction) {
        const items = this.mediaItems();
        const current = this.querySelector('.product-media-modal__content > .active');
        const index = items.indexOf(current);
        if (index < 0) return;
        const next = items[index + direction];
        if (!next) return;

        items.forEach((el) => el.classList.remove('active'));
        next.classList.add('active');
        this.resetZoom();
        next.scrollIntoView();
        this.updateNav();
        this.waitForMedia(next);
      }

      updateNav() {
        const items = this.mediaItems();
        const current = this.querySelector('.product-media-modal__content > .active');
        const index = items.indexOf(current);
        const show = items.length > 1;

        [this.prevButton, this.nextButton].forEach((button) => {
          if (button) button.hidden = !show;
        });
        if (this.prevButton) this.prevButton.disabled = index <= 0;
        if (this.nextButton) this.nextButton.disabled = index < 0 || index >= items.length - 1;
      }

      showLoader() {
        if (this.loader) this.loader.hidden = false;
      }

      hideLoader() {
        if (this.loader) this.loader.hidden = true;
      }

      waitForMedia(activeMedia) {
        this.loaderToken += 1;
        const token = this.loaderToken;
        const finish = () => {
          if (token === this.loaderToken) this.hideLoader();
        };

        const img = activeMedia && activeMedia.tagName === 'IMG' ? activeMedia : activeMedia?.querySelector('img');
        if (!img) {
          finish();
          return;
        }

        img.loading = 'eager';
        if (img.complete && img.naturalWidth > 0) {
          finish();
          return;
        }

        this.showLoader();
        const onDone = () => {
          img.removeEventListener('load', onDone);
          img.removeEventListener('error', onDone);
          finish();
        };
        img.addEventListener('load', onDone);
        img.addEventListener('error', onDone);
      }
    }
  );
}
